import { useRef, useCallback, useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { createPortal } from 'react-dom'
import { Icon } from './Icon'

/* ─── Config ─── */
const LONG_PRESS_MS = 480

interface TabItem {
  path: string
  icon: string
  label: string
}

const LEFT_TABS: TabItem[] = [
  { path: '/', icon: 'public', label: 'nav.plaza' },
  { path: '/explore', icon: 'explore', label: 'nav.explore' },
]

const RIGHT_TABS: TabItem[] = [
  { path: '/history', icon: 'assignment', label: 'nav.desk' },
  { path: '/profile', icon: 'person', label: 'nav.me' },
]

/* ─── Voice Overlay ─── */
function VoiceOverlay({ visible }: { visible: boolean }) {
  if (!visible) return null
  return createPortal(
    <div className="voice-overlay">
      <div className="voice-panel">
        <div className="voice-ring">
          <Icon name="mic" size={32} className="text-primary" />
        </div>
        <p className="text-base font-semibold text-[var(--color-text-primary)]">
          正在聆听…
        </p>
        <p className="text-sm mt-1.5" style={{ color: 'var(--color-text-secondary)' }}>
          松开发送
        </p>
      </div>
    </div>,
    document.body,
  )
}

/* ─── Toast ─── */
let toastTimer: ReturnType<typeof setTimeout> | null = null

function showToast(msg: string) {
  const el = document.getElementById('app-toast')
  if (!el) return
  el.textContent = msg
  el.classList.add('show')
  if (toastTimer) clearTimeout(toastTimer)
  toastTimer = setTimeout(() => el.classList.remove('show'), 2200)
}

export function AppToast() {
  return <div id="app-toast" className="app-toast" role="status" />
}

/* ─── Main TabBar ─── */
export function TabBar() {
  const location = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const [voiceActive, setVoiceActive] = useState(false)
  const longPressRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const suppressClickRef = useRef(false)
  const fabRef = useRef<HTMLButtonElement>(null)

  const isAgentActive = location.pathname.startsWith('/my-agent')

  const fabLabel = isAgentActive
    ? t('nav.holdToTalk', '按住说话')
    : t('nav.myAgent', 'Agent')

  const isActive = useCallback(
    (path: string) =>
      path === '/'
        ? location.pathname === '/'
        : location.pathname.startsWith(path),
    [location.pathname],
  )

  /* ─── Long press handlers ─── */
  const onPointerDown = useCallback((e: React.PointerEvent) => {
    if (e.button !== 0) return
    try { fabRef.current?.setPointerCapture(e.pointerId) } catch {}
    longPressRef.current = setTimeout(() => {
      longPressRef.current = null
      setVoiceActive(true)
      if (navigator.vibrate) navigator.vibrate(12)
    }, LONG_PRESS_MS)
  }, [])

  const onPointerEnd = useCallback(
    (e: React.PointerEvent) => {
      if (longPressRef.current) {
        clearTimeout(longPressRef.current)
        longPressRef.current = null
      }
      if (voiceActive) {
        setVoiceActive(false)
        showToast('语音已发送 (demo)')
        suppressClickRef.current = true
        e.preventDefault()
      }
      try { fabRef.current?.releasePointerCapture(e.pointerId) } catch {}
    },
    [voiceActive],
  )

  const onFabClick = useCallback(() => {
    if (suppressClickRef.current) {
      suppressClickRef.current = false
      return
    }
    navigate('/my-agent')
  }, [navigate])

  useEffect(() => {
    return () => {
      if (longPressRef.current) clearTimeout(longPressRef.current)
    }
  }, [])

  const renderTab = (tab: TabItem) => {
    const active = isActive(tab.path)
    return (
      <button
        key={tab.path}
        onClick={() => navigate(tab.path)}
        className={`flex items-center flex-col gap-0.5 justify-center min-w-[48px] ${
          active ? 'text-primary' : 'text-gray-400 dark:text-gray-500'
        }`}
      >
        <Icon name={tab.icon} size={26} filled={active} />
        <span className={`text-xs ${active ? 'font-semibold' : 'font-medium'}`}>
          {t(tab.label)}
        </span>
      </button>
    )
  }

  return (
    <>
      <nav
        className="fixed bottom-0 left-0 right-0 z-50 border-t border-black/[0.06] dark:border-white/[0.08] md:hidden overflow-visible"
        style={{
          backgroundColor: 'var(--tabbar-bg, #FFFFFF)',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        }}
      >
        <div className="px-4 py-2 overflow-visible">
          <div className="flex justify-between items-center overflow-visible">
            {LEFT_TABS.map(renderTab)}

            {/* ─── Center FAB ─── */}
            <div className="flex flex-col items-center relative" style={{ minWidth: 56 }}>
              <button
                ref={fabRef}
                onClick={onFabClick}
                onPointerDown={onPointerDown}
                onPointerUp={onPointerEnd}
                onPointerCancel={onPointerEnd}
                className={`
                  -mt-7 w-[52px] h-[52px] rounded-full
                  bg-primary flex items-center justify-center
                  shadow-[0_4px_16px_rgba(0,217,160,0.35)]
                  border-[3px] border-white dark:border-[#111113]
                  transition-transform
                  ${voiceActive ? 'scale-[1.08] shadow-[0_0_0_4px_rgba(0,217,160,0.15),0_6px_20px_rgba(0,217,160,0.45)]' : ''}
                `}
                style={{
                  touchAction: 'none',
                  WebkitUserSelect: 'none',
                  userSelect: 'none',
                }}
                aria-label="Agent"
              >
                <Icon name="smart_toy" size={24} filled className="text-white" />
              </button>
              <span
                className={`text-[10px] mt-1 font-medium ${
                  isAgentActive
                    ? 'text-primary font-semibold'
                    : 'text-gray-400 dark:text-gray-500'
                }`}
              >
                {fabLabel}
              </span>
            </div>

            {RIGHT_TABS.map(renderTab)}
          </div>
        </div>
      </nav>

      <VoiceOverlay visible={voiceActive} />
    </>
  )
}
